import { useMemo, useContext, createContext } from 'react';
import { useProfile } from '../components/state/hooks';
import { resolveProfileStates } from '../utils/function';
import { isSchoolStudentRole, isSchoolTeacherRole, isNonSchoolTeacherRole } from '../utils/userAccessHelper';

/**
 * Context for triggering the state selection modal from any consumer.
 * Provided by StateSelectionProvider in App.jsx.
 */
export const StateSelectionContext = createContext({ requestStateSelection: () => {} });

/**
 * Centralized hook for resolving user states from profile.
 *
 * Resolution: profile.states (array) → [profile.state] (string) → null
 *
 * For non-school users with no states, calling requestStateSelection()
 * triggers the StateYearDialogue modal via context.
 */
export const useUserStates = () => {
  const { data: profile, isLoadingdata: isLoading } = useProfile();
  const { requestStateSelection } = useContext(StateSelectionContext);

  const resolved = useMemo(() => resolveProfileStates(profile), [profile]);

  const isSchoolUser = isSchoolStudentRole(profile) || isSchoolTeacherRole(profile);
  const needsStateSelection = !isLoading && resolved === null && !isSchoolUser;

  return {
    states: resolved || [],
    needsStateSelection,
    requestStateSelection,
    isLoading: !!isLoading,
  };
};

/**
 * State scope for the marking-tool subject pickers/filters. Returns the
 * INDIVIDUAL teacher's selected state(s), or [] for everyone else (school
 * teachers/students, non-teachers, or a teacher with no state / profile not
 * loaded yet). Profile-derived so it updates reactively when the teacher's
 * state changes; positively gated on isNonSchoolTeacherRole so scoping only
 * ever narrows the individual-teacher case and is a no-op elsewhere.
 */
export const useMarkingToolStates = () => {
  const { data: profile } = useProfile();
  return useMemo(
    () => (isNonSchoolTeacherRole(profile) ? (resolveProfileStates(profile) || []) : []),
    [profile],
  );
};
